"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Box,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material"
import { Add, Delete, Edit, PlayArrow, Link as LinkIcon, Quiz, VideoLibrary } from "@mui/icons-material"
import axios from "axios"
import { baseUrl, config, requestID, getUser } from "../../common/utils"

interface Course {
  id: string
  title: string
  description: string
  type: "video" | "interactive" | "quiz" | "document"
  duration?: string
  required: boolean
  content?: string // Video URL, interactive link, or document link
    //   status?: "active" | "inactive"
    status?: string
  created_at?: string
  course_id?: string
}

interface CourseManagementProps {
  onCoursesChange?: (courses: Course[]) => void
  selectedCourses?: string[]
  onCourseSelection?: (courseIds: string[]) => void
  selectionMode?: boolean
}

const CourseManagement: React.FC<CourseManagementProps> = ({
  onCoursesChange,
  selectedCourses = [],
  onCourseSelection,
  selectionMode = false,
}) => {
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [addCourseDialogOpen, setAddCourseDialogOpen] = useState(false)
  const [editCourseDialogOpen, setEditCourseDialogOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [addingCourse, setAddingCourse] = useState(false)

  // Form states
  const [newCourseTitle, setNewCourseTitle] = useState("")
  const [newCourseDescription, setNewCourseDescription] = useState("")
  const [newCourseType, setNewCourseType] = useState<"video" | "interactive" | "quiz" | "document">("video")
  const [newCourseDuration, setNewCourseDuration] = useState("")
  const [newCourseRequired, setNewCourseRequired] = useState("0")
  const [newCourseContent, setNewCourseContent] = useState("")

  const busId = "bus_id_b96c58e8adabb47c000dc8dc5c02900f"
  const branchId = "bra_id_b96c58e8adabb47c038dc8dc5c00009m"

  useEffect(() => {
    fetchCourses()
  }, [])

  useEffect(() => {
    if (onCoursesChange) {
      onCoursesChange(courses)
    }
  }, [courses, onCoursesChange])

  const fetchCourses = async () => {
    try {
      setLoading(true)
      const user = getUser().userData
      const response = await axios.post(
        `${baseUrl()}onboardings/getAllCourses`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
        },
        config,
      )

      console.log("Courses response:", response)
      if (response.data.status && Array.isArray(response.data.data)) {
        const coursesData = response.data.data.map((course: any) => ({
          id: course.course_id || course.id,
          course_id: course.course_id || course.id,
          title: course.title || course.name,
          description: course.description || "",
          type: course.type || "video",
          duration: course.duration || "",
          required: course.required === "1" || course.required === true,
          content: course.content || course.video_url || course.link || "",
          status: course.status || "active",
          created_at: course.created_at,
        }))
        setCourses(coursesData)
      } else {
        setCourses([])
      }
    } catch (err) {
      setError("Failed to fetch courses")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddCourse = async () => {
    if (!newCourseTitle.trim() || !newCourseDescription.trim()) {
      setError("Title and description are required")
      return
    }

    try {
        setAddingCourse(true)
        console.log("Adding course with title:", newCourseTitle)
        console.log("New course data:", {
          title: newCourseTitle,
          description: newCourseDescription,
          type: newCourseType,
          duration: newCourseDuration,
          required: newCourseRequired,
          content: newCourseContent,
        })
        const user = getUser().userData
        
      const response = await axios.post(
        `${baseUrl()}onboardings/createCourse`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
          title: newCourseTitle.trim(),
          description: newCourseDescription.trim(),
          type: newCourseType,
          duration: newCourseDuration.trim(),
          required: newCourseRequired,
          content: newCourseContent.trim(),
          created_by: user.user_id,
        },
        config,
      )

      console.log("Create course response:", response)
      if (response.data.status) {
        await fetchCourses()
        resetForm()
        setAddCourseDialogOpen(false)
        setMessage("Course created successfully!")
        setTimeout(() => setMessage(null), 3000)
      } else {
        setError("Failed to create course: " + response.data.message)
      }
    } catch (err) {
      setError("Error creating course")
      console.error(err)
    } finally {
      setAddingCourse(false)
    }
  }

  const handleEditCourse = async () => {
    if (!selectedCourse || !newCourseTitle.trim() || !newCourseDescription.trim()) {
      setError("Title and description are required")
      return
    }

    try {
      setAddingCourse(true)
      const user = getUser().userData
      const response = await axios.post(
        `${baseUrl()}onboardings/updateCourse`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
          course_id: selectedCourse.course_id || selectedCourse.id,
          title: newCourseTitle.trim(),
          description: newCourseDescription.trim(),
          type: newCourseType,
          duration: newCourseDuration.trim(),
          required: newCourseRequired,
          content: newCourseContent.trim(),
          updated_by: user.user_id,
        },
        config,
      )

      console.log("Update course response:", response)
      if (response.data.status) {
        await fetchCourses()
        resetForm()
        setEditCourseDialogOpen(false)
        setSelectedCourse(null)
        setMessage("Course updated successfully!")
        setTimeout(() => setMessage(null), 3000)
      } else {
        setError("Failed to update course: " + response.data.message)
      }
    } catch (err) {
      setError("Error updating course")
      console.error(err)
    } finally {
      setAddingCourse(false)
    }
  }

  const handleDeleteCourse = async (courseId: string) => {
    if (!window.confirm("Are you sure you want to delete this course?")) {
      return
    }

    try {
      const user = getUser().userData
      const response = await axios.post(
        `${baseUrl()}onboardings/deleteCourse`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
          course_id: courseId,
          deleted_by: user.user_id,
        },
        config,
      )

      console.log("Delete course response:", response)
      if (response.data.status) {
        await fetchCourses()
        setMessage("Course deleted successfully!")
        setTimeout(() => setMessage(null), 3000)
      } else {
        setError("Failed to delete course: " + response.data.message)
      }
    } catch (err) {
      setError("Error deleting course")
      console.error(err)
    }
  }

  const handleToggleCourseStatus = async (courseId: string, currentStatus: string) => {
      try {
        console.log("Toggling course status for ID:", courseId, "Current status:", currentStatus)
      const user = getUser().userData
      const newStatus = currentStatus === "0" ? "1" : "0" // Toggle between active (1) and inactive (0)
      const response = await axios.post(
        `${baseUrl()}onboardings/updateCourseStatus`,
        {
          requestID: requestID(),
          business_id: user.bus_id || busId,
          branch_id: user.bra_id || branchId,
          course_id: courseId,
          status: newStatus,
          updated_by: user.user_id,
        },
        config,
      )

      console.log("Toggle course status response:", response)
      if (response.data.status) {
        await fetchCourses()
        setMessage(`Course ${newStatus === "1" ? "activated" : "deactivated"} successfully!`)
        setTimeout(() => setMessage(null), 3000)
      } else {
        setError("Failed to update course status: " + response.data.message)
      }
    } catch (err) {
      setError("Error updating course status")
      console.error(err)
    }
  }

  const openEditDialog = (course: Course) => {
    setSelectedCourse(course)
    setNewCourseTitle(course.title)
    setNewCourseDescription(course.description)
    setNewCourseType(course.type)
    setNewCourseDuration(course.duration || "")
    setNewCourseRequired(course.required ? "1" : "0")
    setNewCourseContent(course.content || "")
    setEditCourseDialogOpen(true)
  }

  const resetForm = () => {
    setNewCourseTitle("")
    setNewCourseDescription("")
    setNewCourseType("video")
    setNewCourseDuration("")
    setNewCourseRequired("0")
    setNewCourseContent("")
  }

  const handleCourseSelectionChange = (courseId: string, checked: boolean) => {
    if (!onCourseSelection) return

    let newSelection = [...selectedCourses]
    if (checked) {
      newSelection.push(courseId)
    } else {
      newSelection = newSelection.filter((id) => id !== courseId)
    }
    onCourseSelection(newSelection)
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <VideoLibrary />
      case "interactive":
        return <LinkIcon />
      case "quiz":
        return <Quiz />
      default:
        return <PlayArrow />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case "video":
        return "primary"
      case "interactive":
        return "secondary"
      case "quiz":
        return "warning"
      default:
        return "default"
    }
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    )
  }

  if (message) {
    return (
      <Alert severity="success" onClose={() => setMessage(null)}>
        {message}
      </Alert>
    )
  }

  return (
    <Box>
      {!selectionMode && (
        <Card>
          <CardHeader
            title="Course Management"
            subheader="Create and manage onboarding courses"
            action={
              <Button variant="contained" startIcon={<Add />} onClick={() => setAddCourseDialogOpen(true)}>
                Add Course
              </Button>
            }
          />
          <CardContent>
            {loading ? (
              <Typography>Loading courses...</Typography>
            ) : courses.length > 0 ? (
              <List>
                {courses.map((course) => (
                  <ListItem key={course.id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTypeIcon(course.type)}
                          <Typography variant="subtitle1">{course.title}</Typography>
                          <Chip label={course.type} size="small" color={getTypeColor(course.type)} variant="outlined" />
                          {course.required && <Chip label="Required" size="small" color="error" />}
                          <Chip
                            label={course.status}
                            size="small"
                            color={course.status === "active" ? "success" : "default"}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {course.description}
                          </Typography>
                          {course.duration && (
                            <Typography variant="caption" color="textSecondary">
                              Duration: {course.duration}
                            </Typography>
                          )}
                          {course.content && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Content: {course.content.substring(0, 50)}...
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() =>
                          handleToggleCourseStatus(course.course_id || course.id, course.status || "active")
                        }
                        size="small"
                        title={course.status === "1" ? "Deactivate" : "Activate"}
                      >
                        <PlayArrow color={course.status === "1" ? "success" : "disabled"} />
                      </IconButton>
                      <IconButton onClick={() => openEditDialog(course)} size="small" title="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteCourse(course.course_id || course.id)}
                        size="small"
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary">
                  No Courses Available
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Create your first course to get started.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {selectionMode && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Select Courses to Send
          </Typography>
          {courses.filter((course) => course.status === "active").length > 0 ? (
            <List>
              {courses
                .filter((course) => course.status === "active")
                .map((course) => (
                  <ListItem key={course.id}>
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course.course_id || course.id)}
                      onChange={(e) => handleCourseSelectionChange(course.course_id || course.id, e.target.checked)}
                      style={{ marginRight: "12px" }}
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTypeIcon(course.type)}
                          <Typography variant="body1">{course.title}</Typography>
                          <Chip label={course.type} size="small" color={getTypeColor(course.type)} variant="outlined" />
                          {course.required && <Chip label="Required" size="small" color="error" />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {course.description}
                          </Typography>
                          {course.duration && (
                            <Typography variant="caption" color="textSecondary">
                              Duration: {course.duration}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No active courses available to send.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create and activate courses first.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Add Course Dialog */}
      <Dialog open={addCourseDialogOpen} onClose={() => setAddCourseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Course</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Course Title"
              value={newCourseTitle}
              onChange={(e) => setNewCourseTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newCourseDescription}
              onChange={(e) => setNewCourseDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Course Type</InputLabel>
              <Select value={newCourseType} onChange={(e) => setNewCourseType(e.target.value as any)}>
                <MenuItem value="video">Video</MenuItem>
                {/* <MenuItem value="interactive">Interactive</MenuItem> */}
                {/* <MenuItem value="quiz">Quiz</MenuItem> */}
                <MenuItem value="document">Document</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Duration (e.g., 30 minutes)"
              value={newCourseDuration}
              onChange={(e) => setNewCourseDuration(e.target.value)}
              fullWidth
            />
            <TextField
              label="Content URL/Link"
              value={newCourseContent}
              onChange={(e) => setNewCourseContent(e.target.value)}
              fullWidth
              placeholder="Video URL, interactive link, or document URL"
            />
            <FormControl fullWidth>
              <InputLabel>Required</InputLabel>
              <Select value={newCourseRequired} onChange={(e) => setNewCourseRequired(e.target.value)}>
                <MenuItem value="0">Optional</MenuItem>
                <MenuItem value="1">Required</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCourseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddCourse}
            disabled={addingCourse || !newCourseTitle.trim() || !newCourseDescription.trim()}
          >
            {addingCourse ? "Creating..." : "Create Course"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={editCourseDialogOpen} onClose={() => setEditCourseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Course</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Course Title"
              value={newCourseTitle}
              onChange={(e) => setNewCourseTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newCourseDescription}
              onChange={(e) => setNewCourseDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Course Type</InputLabel>
              <Select value={newCourseType} onChange={(e) => setNewCourseType(e.target.value as any)}>
                <MenuItem value="video">Video</MenuItem>
                {/* <MenuItem value="interactive">Interactive</MenuItem> */}
                {/* <MenuItem value="quiz">Quiz</MenuItem> */}
                <MenuItem value="document">Document</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Duration (e.g., 30 minutes)"
              value={newCourseDuration}
              onChange={(e) => setNewCourseDuration(e.target.value)}
              fullWidth
            />
            <TextField
              label="Content URL/Link"
              value={newCourseContent}
              onChange={(e) => setNewCourseContent(e.target.value)}
              fullWidth
              placeholder="Video URL, interactive link, or document URL"
            />
            <FormControl fullWidth>
              <InputLabel>Required</InputLabel>
              <Select value={newCourseRequired} onChange={(e) => setNewCourseRequired(e.target.value)}>
                <MenuItem value="0">Optional</MenuItem>
                <MenuItem value="1">Required</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCourseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditCourse}
            disabled={addingCourse || !newCourseTitle.trim() || !newCourseDescription.trim()}
          >
            {addingCourse ? "Updating..." : "Update Course"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CourseManagement
